import React, { useState, useEffect, useRef } from "react";
import { ref, onValue } from "firebase/database";
import { db } from "../firebase";
import "./AuctionLog.css";

const AuctionLog = ({ gameId }) => {
  const [logs, setLogs] = useState([]);
  const [filter, setFilter] = useState("all");
  const logEndRef = useRef(null);

  useEffect(() => {
    if (!gameId) return;

    const logsRef = ref(db, `games/${gameId}/logs`);
    const unsubscribe = onValue(logsRef, (snapshot) => {
      if (snapshot.exists()) {
        const logsData = snapshot.val();
        const logsArray = Object.entries(logsData).map(([id, log]) => ({
          ...log,
          id,
        }));
        // Sort by timestamp ascending (oldest first)
        logsArray.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        setLogs(logsArray);
      } else {
        setLogs([]);
      }
    });

    return () => unsubscribe();
  }, [gameId]);

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [logs]);

  const getLogIcon = (action) => {
    switch (action) {
      case "BID_PLACED":
        return "bi-hand-thumbs-up-fill text-primary";
      case "AUCTION_STARTED":
        return "bi-play-circle-fill text-success";
      case "PLAYER_SOLD":
        return "bi-trophy-fill text-warning";
      case "PLAYER_MANUALLY_ASSIGNED":
        return "bi-person-check-fill text-info";
      case "PLAYER_UNSOLD":
        return "bi-x-circle-fill text-danger";
      case "PLAYER_SKIPPED":
        return "bi-skip-forward-fill text-secondary";
      case "NEXT_PLAYER":
        return "bi-arrow-right-circle-fill text-info";
      case "AUCTION_COMPLETED":
        return "bi-check-circle-fill text-success";
      case "BIDS_CLEARED":
        return "bi-trash-fill text-danger";
      default:
        return "bi-info-circle-fill text-muted";
    }
  };

  const getLogColor = (action) => {
    switch (action) {
      case "BID_PLACED":
        return "log-bid";
      case "AUCTION_STARTED":
        return "log-started";
      case "PLAYER_SOLD":
        return "log-sold";
      case "PLAYER_MANUALLY_ASSIGNED":
        return "log-sold";
      case "PLAYER_UNSOLD":
        return "log-unsold";
      case "PLAYER_SKIPPED":
        return "log-skipped";
      case "NEXT_PLAYER":
        return "log-next";
      case "AUCTION_COMPLETED":
        return "log-completed";
      case "BIDS_CLEARED":
        return "log-cleared";
      default:
        return "log-default";
    }
  };

  const formatLogMessage = (log) => {
    const { action, details } = log;

    switch (action) {
      case "BID_PLACED":
        return (
          <>
            <strong>{details.teamName}</strong> placed a bid of{" "}
            <strong className="text-success">৳{details.amount?.toLocaleString()}</strong> for{" "}
            <strong>{details.playerName}</strong>
          </>
        );
      case "AUCTION_STARTED":
        return (
          <>
            Auction started with{" "}
            <strong>{details.playerName}</strong> (Base: ৳{details.basePrice?.toLocaleString()})
          </>
        );
      case "PLAYER_SOLD":
        return (
          <>
            <strong>{details.playerName}</strong> SOLD to{" "}
            <strong>{details.teamName}</strong> for{" "}
            <strong className="text-success">৳{details.soldPrice?.toLocaleString()}</strong>
          </>
        );
      case "PLAYER_MANUALLY_ASSIGNED":
        return (
          <>
            <strong>{details.playerName}</strong> MANUALLY ASSIGNED to{" "}
            <strong>{details.teamName}</strong> for{" "}
            <strong className="text-success">৳{details.soldPrice?.toLocaleString()}</strong>
            {" "}<span className="badge bg-info">Manual</span>
          </>
        );
      case "PLAYER_UNSOLD":
        return (
          <>
            <strong>{details.playerName}</strong> went UNSOLD (Base: ৳{details.basePrice?.toLocaleString()})
          </>
        );
      case "PLAYER_SKIPPED":
        return (
          <>
            <strong>{details.playerName}</strong> was SKIPPED (Base: ৳{details.basePrice?.toLocaleString()})
          </>
        );
      case "NEXT_PLAYER":
        return (
          <>
            Next up: <strong>{details.playerName}</strong> (Base: ৳{details.basePrice?.toLocaleString()})
          </>
        );
      case "AUCTION_COMPLETED":
        return (
          <>
            <i className="bi bi-check-circle-fill me-1"></i>
            <strong>Auction Completed!</strong> Total: {details.totalPlayers}, Sold: {details.soldCount}, Unsold: {details.unsoldCount}
          </>
        );
      case "BIDS_CLEARED":
        return (
          <>
            <i className="bi bi-trash-fill me-1"></i>
            <strong>Bids Cleared!</strong> All bids for current player removed
          </>
        );
      default:
        return JSON.stringify(details);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const filteredLogs = logs.filter((log) => {
    if (filter === "all") return true;
    return log.action === filter;
  });

  return (
    <div className="card shadow h-100">
      <div className="card-header bg-gradient text-white" style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}>
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            <i className="bi bi-clock-history me-2"></i>
            Auction Activity Log
          </h5>
          <span className="badge bg-white text-primary">{filteredLogs.length}</span>
        </div>
      </div>

      <div className="card-body p-0">
        {/* Filter Bar */}
        <div className="p-3 border-bottom bg-light">
          <div className="btn-group btn-group-sm w-100" role="group">
            <button
              className={`btn ${filter === "all" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => setFilter("all")}
            >
              All
            </button>
            <button
              className={`btn ${filter === "BID_PLACED" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => setFilter("BID_PLACED")}
            >
              <i className="bi bi-hand-thumbs-up"></i> Bids
            </button>
            <button
              className={`btn ${filter === "PLAYER_SOLD" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => setFilter("PLAYER_SOLD")}
            >
              <i className="bi bi-trophy"></i> Sold
            </button>
            <button
              className={`btn ${filter === "PLAYER_UNSOLD" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => setFilter("PLAYER_UNSOLD")}
            >
              <i className="bi bi-x-circle"></i> Unsold
            </button>
          </div>
        </div>

        {/* Log Messages */}
        <div className="log-container" style={{ height: "500px", overflowY: "auto" }}>
          {filteredLogs.length === 0 ? (
            <div className="text-center text-muted py-5">
              <i className="bi bi-inbox display-1"></i>
              <p className="mt-3">No activity logs yet</p>
              <small>Auction activities will appear here in real-time</small>
            </div>
          ) : (
            <div className="p-3">
              {filteredLogs.map((log, index) => (
                <div
                  key={log.id}
                  className={`log-message ${getLogColor(log.action)} animate-slide-in`}
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <div className="d-flex align-items-start">
                    <div className="log-icon me-3">
                      <i className={`bi ${getLogIcon(log.action)} fs-5`}></i>
                    </div>
                    <div className="flex-grow-1">
                      <div className="log-content">
                        {formatLogMessage(log)}
                      </div>
                      <div className="log-time">
                        <i className="bi bi-clock me-1"></i>
                        {formatTime(log.timestamp)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              <div ref={logEndRef} />
            </div>
          )}
        </div>
      </div>

      <div className="card-footer bg-light text-center">
        <small className="text-muted">
          <i className="bi bi-arrow-clockwise me-1"></i>
          Updates in real-time
        </small>
      </div>
    </div>
  );
};

export default AuctionLog;
